import { useState } from 'react';
import { Sliders, ClockCounterClockwise, CaretDown } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { useAssetStore } from '../../stores/assetStore';
import LayerProperties from './LayerProperties';
import AssetInspector from './AssetInspector';
import BatchPropertiesPanel from './BatchPropertiesPanel';
import UndoHistoryPanel from './UndoHistoryPanel';
import GroupInspector from './GroupInspector';

function SceneNotesField() {
  const { sceneNotes, setSceneNotes } = useSceneStore();
  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">
        Scene Notes
      </label>
      <textarea
        data-testid="scene-notes-input"
        value={sceneNotes}
        onChange={(e) => setSceneNotes(e.target.value)}
        placeholder="Add notes for your team…"
        rows={4}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full bg-bg-input border border-border text-text-primary rounded-md px-2.5 py-2 text-xs font-sans placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-all duration-normal resize-none"
      />
    </div>
  );
}

export default function InspectorPanel({ viewOnly }: { viewOnly?: boolean }) {
  const { selectedLayerId, selectedLayerIds, selectedGroupId, undoStack, redoStack } = useSceneStore();
  const { selectedAssetId } = useAssetStore();
  const [historyOpen, setHistoryOpen] = useState(false);

  const isMultiSelect = selectedLayerIds.length > 1;
  const historyCount = undoStack.length + redoStack.length;

  return (
    <div className="flex flex-col h-full bg-bg-surface">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle flex items-center gap-2 shrink-0">
        <Sliders size={14} className="text-accent" />
        <span className="font-serif text-base font-medium text-accent tracking-[0.01em]">Inspector</span>
        {isMultiSelect && (
          <span className="ml-auto text-[10px] font-mono text-accent bg-accent-muted px-2 py-0.5 rounded-full">
            {selectedLayerIds.length} selected
          </span>
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden relative ${viewOnly ? 'pointer-events-none select-text' : ''}`}>
        {viewOnly && (
          <div className="sticky top-0 z-10 bg-warning-muted/80 border-b border-warning/20 px-3 py-1.5 flex items-center gap-1.5">
            <span className="font-sans text-[10px] text-warning font-medium">View only — values are read-only</span>
          </div>
        )}
        {isMultiSelect ? (
          <BatchPropertiesPanel />
        ) : selectedGroupId ? (
          <GroupInspector groupId={selectedGroupId} />
        ) : selectedLayerId ? (
          <LayerProperties />
        ) : selectedAssetId ? (
          <AssetInspector />
        ) : (
          <div className="p-4 text-text-tertiary text-xs font-sans">
            Select a layer or asset to inspect
          </div>
        )}

        <SceneNotesField />

        {/* Undo History collapsible section */}
        <div className="border-t border-border-subtle">
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-bg-hover transition-colors"
          >
            <ClockCounterClockwise size={13} className="text-text-tertiary" />
            <span className="text-xs font-sans font-medium text-text-secondary flex-1 text-left">History</span>
            {historyCount > 0 && (
              <span className="text-[10px] font-mono text-text-tertiary">{historyCount}</span>
            )}
            <CaretDown size={10} className={`text-text-tertiary transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
          </button>
          {historyOpen && <UndoHistoryPanel />}
        </div>
      </div>
    </div>
  );
}
